import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

import {
  DateRangePreset,
  RelativeDateOffset,
  SelectOption,
} from '@drivingdoc/shared-types/reports';

// Enable quarter support for dayjs
dayjs.extend(quarterOfYear);

/**
 * Calculate a date from a relative offset
 */
export function calculateDateFromOffset(offset: RelativeDateOffset): Dayjs {
  return dayjs().add(offset.value, offset.unit);
}

/**
 * Calculate date range from a preset
 */
export function calculatePresetDateRange(preset: DateRangePreset): { start: Dayjs | null; end: Dayjs | null } {
  const today = dayjs();

  switch (preset) {
    case 'all_time':
      return { start: null, end: null };
    case 'today':
      return { start: today, end: today };
    case 'yesterday':
      return { start: today.subtract(1, 'day'), end: today.subtract(1, 'day') };
    case 'this_week':
      return { start: today.startOf('week'), end: today.endOf('week') };
    case 'last_week':
      return {
        start: today.subtract(1, 'week').startOf('week'),
        end: today.subtract(1, 'week').endOf('week'),
      };
    case 'this_month':
      return { start: today.startOf('month'), end: today.endOf('month') };
    case 'last_month':
      return {
        start: today.subtract(1, 'month').startOf('month'),
        end: today.subtract(1, 'month').endOf('month'),
      };
    case 'this_quarter':
      return { start: today.startOf('quarter'), end: today.endOf('quarter') };
    case 'last_quarter':
      return {
        start: today.subtract(1, 'quarter').startOf('quarter'),
        end: today.subtract(1, 'quarter').endOf('quarter'),
      };
    case 'this_year':
      return { start: today.startOf('year'), end: today.endOf('year') };
    case 'last_year':
      return {
        start: today.subtract(1, 'year').startOf('year'),
        end: today.subtract(1, 'year').endOf('year'),
      };
    case 'last_7_days':
      return { start: today.subtract(7, 'days'), end: today };
    case 'last_30_days':
      return { start: today.subtract(30, 'days'), end: today };
    case 'last_90_days':
      return { start: today.subtract(90, 'days'), end: today };
  }
}

/**
 * Convert string[] default value to SelectOption[]
 */
export function resolveMultiSelectDefault(
  defaultValues: string[],
  options: SelectOption[]
): SelectOption[] {
  return options.filter((opt) => defaultValues.includes(opt.value));
}

/**
 * Get preset label for display
 */
export function getPresetLabel(preset: DateRangePreset): string {
  const labels: Record<DateRangePreset, string> = {
    all_time: 'All Time',
    today: 'Today',
    yesterday: 'Yesterday',
    this_week: 'This Week',
    last_week: 'Last Week',
    this_month: 'This Month',
    last_month: 'Last Month',
    this_quarter: 'This Quarter',
    last_quarter: 'Last Quarter',
    this_year: 'This Year',
    last_year: 'Last Year',
    last_7_days: 'Last 7 Days',
    last_30_days: 'Last 30 Days',
    last_90_days: 'Last 90 Days',
  };
  return labels[preset];
}
